import type { Channel, CustomerRecord, SessionLength } from '@/domain/types';
import { dmSendMode } from '@/domain/dmSendMode';

/**
 * Where a deposit link from the deposit modal goes (tv-isee). Deposit links go out
 * in active chats only (Anas, 1 Oct), so only the normal < 24h reply route is used:
 * never the human-agent tag or the 7-day handoff. Plans and pay-at-studio holds
 * stay local. `nowIso` MUST be wall time (see dmSendMode).
 */
export type DepositLinkRoute = 'instagram' | 'window-closed' | 'local';

export function depositLinkRoute(a: {
  customer: Pick<CustomerRecord, 'conversation'>;
  channel: Channel;
  planEnabled: boolean;
  holdOnly: boolean;
  nowIso: string;
}): DepositLinkRoute {
  if (a.channel !== 'ig-dm' || a.planEnabled || a.holdOnly) return 'local';
  const { mode } = dmSendMode(a.customer, 'ig-dm', a.nowIso);
  if (mode === 'simulated') return 'local';
  return mode === 'reply' ? 'instagram' : 'window-closed';
}

/** The deposit-link message: the modal's preview text plus the link. Preview and send share it. */
export function depositLinkMessage(a: { firstName: string; sessionLabel: string; amountAud: string; expiryHours: number; url: string }): string {
  return `Hey ${a.firstName}, here's your deposit link for the ${a.sessionLabel.toLowerCase()} session: ${a.amountAud}. The link expires in ${a.expiryHours}h. Once you've paid we'll lock in a time that works for you.\n${a.url}`;
}

const SESSION_WORDS: Record<SessionLength, string> = {
  small: 'small',
  'half-day': 'half day',
  'full-day': 'full day',
  'multi-session': 'multi-session',
};

/** The resend-on-an-expired-link message (tv-isee spec): what the customer receives, plus the link. */
export function depositResendMessage(a: { firstName: string; sessionLength: SessionLength; amountAud: string; url: string }): string {
  return `Hey ${a.firstName}, here's a fresh deposit link for your ${SESSION_WORDS[a.sessionLength]} session: ${a.amountAud}.\n${a.url}`;
}

/**
 * The modal's route note (tv-isee): what happens to the link on 'instagram' and
 * 'window-closed'; null on 'local'. `handle` falls back to the customer's name;
 * `nowIso` MUST be wall time, the same instant the route was computed with.
 */
export function depositLinkRouteNote(a: {
  route: DepositLinkRoute;
  customer: Pick<CustomerRecord, 'conversation' | 'instagramHandle'>;
  fallbackName: string;
  nowIso: string;
}): string | null {
  if (a.route === 'local') return null;
  const handle = a.customer.instagramHandle?.trim().replace(/^@+/, '') || a.fallbackName;
  if (a.route === 'window-closed') {
    return `@${handle} last messaged over 24 hours ago. Sending holds the slot and puts this message in the message box. Check it there and send it as yourself.`;
  }
  const hours = Math.max(1, Math.floor(dmSendMode(a.customer, 'ig-dm', a.nowIso).hoursLeft));
  return `Sends to @${handle} on Instagram. Reply window open for ${hours} more ${hours === 1 ? 'hour' : 'hours'}.`;
}
